class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.sentinelObserver = null;
    this.boundaryObserver = null;
    this.autoTriggerEnabled = true;
    this.isFetching = false;
  }

  connectedCallback() {
    this.classList.add('infinite-scroll--enhanced');

    this.grid = this.querySelector('#product-grid');
    this.sentinel = this.querySelector('[data-tc-sentinel]');
    this.loadMoreBtn = this.querySelector('[data-tc-load-more]');
    this.errorEl = this.querySelector('[data-tc-error]');
    this.endEl = this.querySelector('[data-tc-end]');
    this.statusEl = this.querySelector('[data-tc-status]');
    this.nextLink = this.querySelector('[data-tc-next]');

    if (!this.grid || !this.sentinel || !this.loadMoreBtn) return;

    this.loadMoreBtn.addEventListener('click', () => {
      this.autoTriggerEnabled = true;
      this.loadNext();
    });

    this.setupSentinelObserver();
    this.setupBoundaryObserver();

    if (!this.nextLink) {
      this.markEnd();
    }
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.sentinelObserver) this.sentinelObserver.disconnect();
    if (this.boundaryObserver) this.boundaryObserver.disconnect();
  }

  setupSentinelObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.sentinelObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            this.autoTriggerEnabled &&
            !this.isFetching &&
            this.nextLink
          ) {
            this.loadNext();
          }
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 900px 0px',
        threshold: 0,
      }
    );

    this.sentinelObserver.observe(this.sentinel);
  }

  setupBoundaryObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.boundaryObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const page = entry.target.dataset.page;
            if (page) this.replaceUrlForPage(page);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    this.observeBoundariesInDom();
  }

  observeBoundariesInDom() {
    const seenPages = new Set();
    const items = this.grid.querySelectorAll('li.grid__item');
    for (const li of items) {
      const page = li.dataset.page;
      if (page && !seenPages.has(page)) {
        seenPages.add(page);
        this.boundaryObserver.observe(li);
      }
    }
  }

  async loadNext() {
    if (this.isFetching || !this.nextLink) return;

    const url = this.nextLink.href;
    this.isFetching = true;
    this.setLoadMoreState('loading');
    this.announce('Loading more products');

    this.abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      this.appendFromHtml(html);
      this.isFetching = false;
      this.setLoadMoreState('idle');
      this.autoTriggerEnabled = true;
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.isFetching = false;
      this.setLoadMoreState('error');
      this.autoTriggerEnabled = false;
    }
  }

  markEnd() {
    if (this.sentinelObserver) {
      this.sentinelObserver.disconnect();
      this.sentinelObserver = null;
    }
    if (this.sentinel) this.sentinel.hidden = true;
    if (this.loadMoreBtn) this.loadMoreBtn.hidden = true;
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.endEl) {
      this.endEl.hidden = false;
      const totalEl = this.endEl.querySelector('[data-tc-total]');
      if (totalEl) {
        this.announce(`End of results — showing all ${totalEl.textContent} products`);
      }
    }
  }

  appendFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newItems = doc.querySelectorAll('#product-grid > li.grid__item');
    newItems.forEach((li) => {
      this.grid.appendChild(li);
      this.observeBoundary(li);
    });

    const newNext = doc.querySelector('[data-tc-next]');
    if (newNext) {
      this.nextLink.href = newNext.getAttribute('href');
    } else {
      this.nextLink.remove();
      this.nextLink = null;
      this.markEnd();
    }

    const total = doc.querySelector('infinite-scroll')?.dataset.total;
    if (total) {
      this.announce(`Showing ${this.grid.children.length} of ${total} products`);
    }
  }

  setLoadMoreState(state) {
    this.loadMoreBtn.disabled = state === 'loading';
    this.errorEl.hidden = state !== 'error';
    if (state === 'idle') this.loadMoreBtn.textContent = 'Load more';
    if (state === 'loading') this.loadMoreBtn.textContent = 'Loading…';
    if (state === 'error') this.loadMoreBtn.textContent = 'Try again';
  }

  announce(msg) {
    if (this.statusEl) this.statusEl.textContent = msg;
  }

  observeBoundary(li) {
    if (!this.boundaryObserver) return;
    const page = li.dataset.page;
    if (!page) return;
    // Only observe the first card of each page; skip the rest.
    const firstOfPage = this.grid.querySelector(`li.grid__item[data-page="${page}"]`);
    if (firstOfPage === li) {
      this.boundaryObserver.observe(li);
    }
  }

  replaceUrlForPage(page) {
    const url = new URL(window.location.href);
    if (page === '1') {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page);
    }
    const newPath = url.pathname + (url.search ? url.search : '') + url.hash;
    if (newPath !== window.location.pathname + window.location.search + window.location.hash) {
      history.replaceState(history.state, '', newPath);
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
